
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { CellForVSelect, ListItem, ListItemType } from './todolist.model';
import Entry from './entry/Entry';

@Component({
  name: 'TodoList',
  components: {
    Entry,
  },
})
export default class TodoList extends Vue {
  @Prop({ required: true })
  readonly json!: string;
  @Prop({ required: true })
  readonly cells!: CellForVSelect[];
  @Prop({ required: true })
  readonly editable!: boolean;
  @Prop({ default: false })
  readonly editorMode!: boolean;
  @Prop({ default: () => ((_: ListItem) => true) })
  readonly canDeleteItemFn!: (listItem: ListItem) => boolean;

  newTodoText: string = '';
  newType: ListItemType = ListItemType.Default;

  get remaining(): number {
    // Headers are structural, never count as "remaining work"
    return this.todos
        .filter(todo => todo.type !== ListItemType.Header && !todo.completed)
        .length;
  }

  get progressPercentage(): number {
    if (this.todos === undefined || this.todos.length === 0) {
      return 0;
    }

    // Exclude headers entirely from progress calculation
    const actionableItems = this.todos.filter(todo => todo.type !== ListItemType.Header);
    if (actionableItems.length === 0) {
      return 0; // Only headers exist, nothing to complete
    }
    const completedCount = actionableItems.filter(todo => todo.completed).length;

    return Math.round((completedCount / actionableItems.length) * 100);
  }

  get todos(): ListItem[] {
    return JSON.parse(this.json) as ListItem[];
  }

  get types(): { id: number; name: string }[] {
    return [
      { id: ListItemType.Default, name: this.$t('todoTypeDefault').toString() },
      { id: ListItemType.Comment, name: this.$t('todoTypeComment').toString() },
      { id: ListItemType.Header, name: this.$t('todoTypeHeader').toString() },
      { id: ListItemType.RadioGroup, name: this.$t('todoTypeRadioGroup').toString() },
      { id: ListItemType.ScanCell, name: this.$t('todoTypeScanCell').toString() },
      { id: ListItemType.Signature, name: this.$t('todoTypeSignature').toString() },
    ];
  }

  getTypeName(type?: ListItemType): string {
    if (type === undefined || type === null || type === ListItemType.Default) {
      return '';
    }
    const found = this.types.find(e => e.id === type);
    return found ? found.name : '';
  }

  showTypeBadge(type?: ListItemType): boolean {
    return type !== undefined && type !== null && type !== ListItemType.Default;
  }

  getTypeBorderClass(type?: ListItemType): Record<string, boolean> {
    if (!type || type === ListItemType.Default) {
      return {};
    }
    return {
      'border-comment': type === ListItemType.Comment,
      'border-header': type === ListItemType.Header,
      'border-scancell': type === ListItemType.ScanCell,
      'border-signature': type === ListItemType.Signature,
    };
  }

  getTypeBadgeClass(type?: ListItemType): Record<string, boolean> {
    if (!type || type === ListItemType.Default) {
      return {};
    }
    return {
      'badge-comment': type === ListItemType.Comment,
      'badge-header': type === ListItemType.Header,
      'badge-scancell': type === ListItemType.ScanCell,
      'badge-signature': type === ListItemType.Signature,
    };
  }

  addTodo(): void {
    if ((this.newTodoText.trim().length ?? 0) === 0) {
      return;
    }

    const checked = this.newType === ListItemType.Header;
    const maxId = this.todos.map(e => e.id!).reduce((l, r) => Math.max(l, r), 0);
    const newItem = new ListItem(
        maxId + 1,
        this.newTodoText,
        checked,
        false,
        this.newType,
        '',
    );
    const newList = [...this.todos, newItem];
    this.newTodoText = '';
    this.notifyChange(newList);
  }

  removeTodo(todo: ListItem): void {
    if (!this.canDeleteItemFn(todo)) {
      return;
    }

    const newList = [...this.todos];
    newList.splice(this.todos.indexOf(todo), 1);
    this.notifyChange(newList);
  }

  private notifyChange(list: ListItem[]): void {
    this.$emit('change', JSON.stringify(list));
  }
}
